import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { GoHoverTooltip } from '../../go-config/components/GoHoverTooltip';
import { getApiBaseUrl } from '../../../utils/apiConfig';

function formatNumber(n) {
  if (n == null || Number.isNaN(Number(n))) return '0';
  return Number(n).toLocaleString('pt-BR');
}

/**
 * Popover de estoque — visual do Novo painel (Plus Jakarta / violet).
 */
export default function TechnicianPanelPartStockPopover({
  open,
  onClose,
  productCode,
  productName,
  anchorRef,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stockData, setStockData] = useState(null);
  const [showByCompany, setShowByCompany] = useState(false);
  const [showSimilar, setShowSimilar] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open || !productCode) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setStockData(null);
    setShowByCompany(false);
    setShowSimilar(false);
    (async () => {
      try {
        const token = localStorage.getItem('authToken');
        const res = await fetch(`${getApiBaseUrl()}/api/products/${encodeURIComponent(productCode)}/stock`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        const result = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (result.success) setStockData(result);
        else setError(result.message || 'Erro ao carregar dados de estoque');
      } catch {
        if (!cancelled) setError('Erro ao carregar dados de estoque');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, productCode]);

  useEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const anchor = anchorRef?.current;
      if (!anchor) return;
      const panelW = 340;
      const panelH = panelRef.current?.offsetHeight || 220;
      const rect = anchor.getBoundingClientRect();
      let left = rect.left - panelW - 12;
      if (left < 8) left = Math.min(rect.right + 12, window.innerWidth - panelW - 8);
      let top = rect.top;
      if (top + panelH > window.innerHeight - 8) {
        top = Math.max(8, window.innerHeight - panelH - 8);
      }
      setPos({ top, left });
    };
    place();
    const t = requestAnimationFrame(place);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      cancelAnimationFrame(t);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, anchorRef, loading, stockData, showByCompany, showSimilar]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (panelRef.current?.contains(e.target)) return;
      if (anchorRef?.current?.contains(e.target)) return;
      onClose?.();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose?.();
      }
    };
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('keydown', onKey, true);
    };
  }, [open, onClose, anchorRef]);

  if (!open) return null;

  const totals = stockData?.totals;

  return createPortal(
    <div
      ref={panelRef}
      className="tpm-stock-pop"
      style={{ top: pos.top, left: pos.left }}
      role="dialog"
      aria-label="Informações de estoque"
    >
      <div className="tpm-stock-pop-head">
        <div className="tpm-stock-pop-title-wrap">
          <span className="tpm-stock-pop-title">{productName || 'Disponibilidade em estoque'}</span>
          {totals && (
            <span className="tpm-stock-pop-total">Total {formatNumber(totals.total)}</span>
          )}
        </div>
        <button type="button" className="tpm-stock-pop-close" onClick={onClose} aria-label="Fechar">
          <svg width="11" height="11" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.6" fill="none">
            <path d="M5 5l14 14M19 5L5 19" />
          </svg>
        </button>
      </div>

      {loading ? (
        <div className="tpm-stock-pop-status">Carregando…</div>
      ) : error ? (
        <div className="tpm-stock-pop-status tpm-stock-pop-status--err">{error}</div>
      ) : stockData ? (
        <div className="tpm-stock-pop-body">
          <div className="tpm-stock-pop-table">
            <div className="tpm-stock-pop-row tpm-stock-pop-row--head">
              <span>Tipo</span>
              <span>Comércio</span>
              <span>Imobilizado</span>
              <span>RMA</span>
            </div>
            <div className="tpm-stock-pop-row tpm-stock-pop-row--total">
              <span>Total</span>
              <span>{formatNumber(totals?.comercio || 0)}</span>
              <span>{formatNumber(totals?.imobilizado || 0)}</span>
              <span>{formatNumber(totals?.rma || 0)}</span>
            </div>
          </div>

          {Array.isArray(stockData.data) && stockData.data.length > 0 && (
            <>
              <div className="tpm-stock-pop-actions">
                <button
                  type="button"
                  className={`tpm-stock-pop-action${showByCompany ? ' is-on' : ''}`}
                  onClick={() => setShowByCompany((v) => !v)}
                >
                  {showByCompany ? '−' : '+'} Estoque por empresa
                </button>
                {stockData.similarProducts?.length > 0 && (
                  <button
                    type="button"
                    className={`tpm-stock-pop-action${showSimilar ? ' is-on' : ''}`}
                    onClick={() => setShowSimilar((v) => !v)}
                  >
                    {showSimilar ? '−' : '+'} Produtos semelhantes
                  </button>
                )}
              </div>

              {showByCompany && (
                <div className="tpm-stock-pop-table tpm-stock-pop-table--nested">
                  <div className="tpm-stock-pop-row tpm-stock-pop-row--head">
                    <span>Emp</span>
                    <span>Comércio</span>
                    <span>Imobilizado</span>
                    <span>RMA</span>
                  </div>
                  {stockData.data.map((item, idx) => (
                    <div key={idx} className="tpm-stock-pop-row">
                      <GoHoverTooltip
                        text={item.empresaNome || undefined}
                        align="start"
                        wrap
                        onlyWhenTruncated
                        className="tpm-stock-emp-tip"
                      >
                        <span>{item.empresa || 'N/A'}</span>
                      </GoHoverTooltip>
                      <span className={item.comercio < 0 ? 'is-neg' : ''}>{formatNumber(item.comercio)}</span>
                      <span className={item.imobilizado < 0 ? 'is-neg' : ''}>{formatNumber(item.imobilizado)}</span>
                      <span className={item.rma < 0 ? 'is-neg' : ''}>{formatNumber(item.rma)}</span>
                    </div>
                  ))}
                </div>
              )}

              {showSimilar && stockData.similarProducts?.length > 0 && (
                <div className="tpm-stock-pop-table tpm-stock-pop-table--nested tpm-stock-pop-table--similar">
                  <div className="tpm-stock-pop-row tpm-stock-pop-row--head tpm-stock-pop-row--similar">
                    <span>Cód.</span>
                    <span>Produto</span>
                    <span>Total</span>
                  </div>
                  {stockData.similarProducts.map((p, idx) => (
                    <div key={idx} className="tpm-stock-pop-row tpm-stock-pop-row--similar">
                      <span>{p.codigo || p.code || '—'}</span>
                      <span>{p.nome || p.name || '—'}</span>
                      <span>{formatNumber(p.total ?? p.estoque ?? 0)}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      ) : null}
    </div>,
    document.body
  );
}
